/**
 * SavedViewsBar — named-view switcher for Operator Lists v2.
 * Lives in the list More menu, and only when at least one named view exists.
 * "Save view" is `SaveViewControl` in the same menu.
 */
import { useState, type KeyboardEvent } from 'react'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import IconButton from '@mui/material/IconButton'
import TextField from '@mui/material/TextField'
import AddIcon from '@mui/icons-material/Add'
import CheckIcon from '@mui/icons-material/Check'
import CloseIcon from '@mui/icons-material/Close'
import type { SavedView } from './useSavedViews'

export interface SavedViewsBarProps {
  views: SavedView[]
  /** Currently applied saved view id, or null when "Default" is active. */
  activeId: string | null
  /** Fired with the clicked view, or null when the Default tab was clicked. */
  onApply: (view: SavedView | null) => void
  onRemove: (id: string) => void
}

export interface SaveViewControlProps {
  onSave: (name: string) => void
}

/** Shared tab-row visual: active tabs get a token-driven accent underline. */
function tabSx(active: boolean) {
  return (theme: import('@mui/material/styles').Theme) => ({
    display: 'flex',
    alignItems: 'center',
    gap: 0.25,
    px: 1.25,
    py: 0.5,
    fontSize: 13,
    lineHeight: '20px',
    cursor: 'pointer',
    userSelect: 'none' as const,
    whiteSpace: 'nowrap' as const,
    borderBottom: '2px solid',
    borderColor: active ? theme.palette.primary.main : 'transparent',
    color: active ? theme.palette.text.primary : theme.palette.text.secondary,
    fontWeight: active ? 600 : 400,
    '&:hover': { color: theme.palette.text.primary },
    '&:hover .saved-view-remove, &:focus-within .saved-view-remove': { opacity: 1 },
  })
}

export function SaveViewControl({ onSave }: SaveViewControlProps) {
  const [adding, setAdding] = useState(false)
  const [name, setName] = useState('')

  const commit = () => {
    const trimmed = name.trim()
    if (trimmed) onSave(trimmed)
    setName('')
    setAdding(false)
  }

  const cancel = () => {
    setName('')
    setAdding(false)
  }

  const handleNameKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      commit()
    } else if (e.key === 'Escape') {
      e.preventDefault()
      cancel()
    }
  }

  if (adding) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 1.5, py: 0.75 }}>
        <TextField
          autoFocus
          size="small"
          placeholder="View name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={handleNameKeyDown}
          sx={{ '& .MuiInputBase-root': { fontSize: 12, height: 28 } }}
        />
        <IconButton aria-label="Confirm save view" size="small" onClick={commit}>
          <CheckIcon sx={{ fontSize: 16 }} />
        </IconButton>
        <IconButton aria-label="Cancel save view" size="small" onClick={cancel}>
          <CloseIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Box>
    )
  }

  return (
    <Button
      variant="text"
      size="small"
      startIcon={<AddIcon sx={{ fontSize: 15 }} />}
      onClick={() => setAdding(true)}
      sx={{
        textTransform: 'none',
        fontSize: 14,
        color: 'text.primary',
        justifyContent: 'flex-start',
        px: 1.5,
        py: 0.75,
        width: '100%',
      }}
    >
      Save view
    </Button>
  )
}

export function SavedViewsBar({ views, activeId, onApply, onRemove }: SavedViewsBarProps) {
  if (views.length === 0) return null

  const tabKeyDown = (e: KeyboardEvent<HTMLDivElement>, action: () => void) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      action()
    }
  }

  return (
    <Box
      role="tablist"
      aria-label="Saved views"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        gap: 0,
        px: 0.5,
        py: 0.25,
      }}
    >
      <Box
        role="tab"
        tabIndex={0}
        aria-selected={activeId === null}
        onClick={() => onApply(null)}
        onKeyDown={(e) => tabKeyDown(e, () => onApply(null))}
        sx={tabSx(activeId === null)}
      >
        Default
      </Box>

      {views.map((v) => {
        const active = activeId === v.id
        return (
          <Box
            key={v.id}
            role="tab"
            tabIndex={0}
            aria-selected={active}
            onClick={() => onApply(v)}
            onKeyDown={(e) => tabKeyDown(e, () => onApply(v))}
            sx={tabSx(active)}
          >
            <Box component="span">{v.name}</Box>
            <IconButton
              className="saved-view-remove"
              aria-label={`Remove ${v.name}`}
              size="small"
              onClick={(e) => {
                e.stopPropagation()
                onRemove(v.id)
              }}
              sx={{ p: 0.25, opacity: 0, transition: 'opacity 0.15s' }}
            >
              <CloseIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Box>
        )
      })}
    </Box>
  )
}
